import { env } from "@/lib/env";
import { ApiError, isRecord } from "./errors";
import { isRecordingResponses, recordLogEntry, recordResponse, redactBody } from "./request-log";
import { tokens } from "./tokens";
import type { TokenPair } from "./types";

export type QueryValue = string | number | boolean | undefined | null;

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  /** JSON-encoded unless it is FormData / Blob / ArrayBuffer. */
  body?: unknown;
  query?: Record<string, QueryValue | QueryValue[]>;
  signal?: AbortSignal;
  /** Attach the bearer token. Default true. */
  auth?: boolean;
  headers?: Record<string, string>;
  /** Return the raw Response instead of parsing (downloads). */
  raw?: boolean;
}

function buildUrl(path: string, query?: RequestOptions["query"]) {
  const url = `${env.apiBase}${path.startsWith("/") ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      for (const v of value) if (v !== undefined && v !== null && v !== "") params.append(key, String(v));
    } else {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

function isBinaryBody(body: unknown): body is BodyInit {
  return (
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    body instanceof URLSearchParams ||
    (typeof ReadableStream !== "undefined" && body instanceof ReadableStream)
  );
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204 || res.status === 205) return undefined;
  const type = res.headers.get("content-type") ?? "";
  if (!type.includes("json")) {
    const text = await res.text();
    return text.length ? text : undefined;
  }
  try {
    return await res.json();
  } catch {
    return undefined;
  }
}

function toApiError(res: Response, body: unknown): ApiError {
  const requestId = res.headers.get("X-Request-Id");
  const retryAfterRaw = res.headers.get("Retry-After");
  const retryAfterSeconds = retryAfterRaw ? Number.parseInt(retryAfterRaw, 10) : null;

  if (isRecord(body) && isRecord(body.error)) {
    const err = body.error;
    return new ApiError({
      code: typeof err.code === "string" ? err.code : "unknown",
      message: typeof err.message === "string" ? err.message : res.statusText,
      status: res.status,
      details: err.details,
      requestId,
      retryAfterSeconds: Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : null,
    });
  }

  return new ApiError({
    code: res.status === 401 ? "unauthenticated" : res.status >= 500 ? "internal_error" : "unknown",
    message: typeof body === "string" && body ? body : res.statusText || "Request failed",
    status: res.status,
    requestId,
    retryAfterSeconds: Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : null,
  });
}

/* ---------------------------------------------------------------------------
   Refresh, serialised behind a single in-flight promise.

   Several queries can 401 in the same tick. If each fired its own refresh, one
   would win and the rest would burn a single-use token and come back
   token_invalid — so every caller awaits the same promise.
--------------------------------------------------------------------------- */

let inFlightRefresh: Promise<void> | null = null;

async function postRefresh(refreshToken: string): Promise<TokenPair> {
  const res = await fetch(buildUrl("/v1/auth/refresh"), {
    method: "POST",
    credentials: "omit",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ refreshToken, platform: "web" }),
  });
  const body = await parseBody(res);
  if (!res.ok) throw toApiError(res, body);
  return body as TokenPair;
}

async function doRefresh(): Promise<void> {
  const stored = tokens.getRefresh();
  if (!stored) {
    throw new ApiError({ code: "token_invalid", message: "No refresh token", status: 401 });
  }

  try {
    const pair = await postRefresh(stored);
    tokens.set(pair);
  } catch (e) {
    // Another tab may have rotated the token between our read and the request.
    // If what's in storage has changed, that rotation is the newer truth.
    const latest = tokens.getRefresh();
    if (latest && latest !== stored) {
      try {
        const pair = await postRefresh(latest);
        tokens.set(pair);
        return;
      } catch {
        /* fall through to ending the session */
      }
    }
    tokens.endSession();
    throw e;
  }
}

export function refreshSession(): Promise<void> {
  inFlightRefresh ??= doRefresh().finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
}

/* ------------------------------------------------------------------------- */

async function send(path: string, opts: RequestOptions, accessToken: string | null) {
  const headers: Record<string, string> = { Accept: "application/json", ...opts.headers };
  let body: BodyInit | undefined;

  if (opts.body !== undefined && opts.body !== null) {
    if (isBinaryBody(opts.body)) {
      // Let the browser set the boundary/content-type for FormData.
      body = opts.body;
    } else {
      headers["Content-Type"] ??= "application/json";
      body = JSON.stringify(opts.body);
    }
  }

  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  return fetch(buildUrl(path, opts.query), {
    method: opts.method ?? "GET",
    headers,
    body,
    signal: opts.signal,
    // Bearer auth only — never send ambient cookies to the API.
    credentials: "omit",
    mode: "cors",
    referrerPolicy: "strict-origin-when-cross-origin",
    cache: "no-store",
  });
}

/**
 * The one place every HTTP call goes through. Handles the envelope, the
 * refresh-and-retry-once dance, and turning failures into ApiError.
 */
export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const useAuth = opts.auth !== false;
  const startedAt = performance.now();

  // Proactive refresh: if we know the access token is stale, renew before
  // spending a round trip on a guaranteed 401.
  if (useAuth && !tokens.hasFreshAccess() && tokens.getRefresh()) {
    try {
      await refreshSession();
    } catch {
      /* fall through — the request will 401 and be handled below */
    }
  }

  let res: Response;
  try {
    res = await send(path, opts, useAuth ? tokens.getAccess() : null);
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") {
      throw new ApiError({ code: "aborted", message: "Request cancelled", status: 0 });
    }
    throw logFailure(
      path,
      opts,
      new ApiError({
        code: "network_error",
        message: `Network request failed — could not reach ${env.apiOrigin}. If this is the only error, it is usually CORS: the API must allow this origin.`,
        status: 0,
      }),
      startedAt,
    );
  }

  if (res.status === 401 && useAuth) {
    const peeked = await parseBody(res);
    const code = isRecord(peeked) && isRecord(peeked.error) ? peeked.error.code : null;

    if (code === "token_expired" && tokens.getRefresh()) {
      await refreshSession(); // throws (and ends the session) if it fails
      try {
        res = await send(path, opts, tokens.getAccess());
      } catch {
        throw new ApiError({ code: "network_error", message: "Network request failed", status: 0 });
      }
      if (!res.ok) throw logFailure(path, opts, toApiError(res, await parseBody(res)), startedAt);
      return finish<T>(res, opts, path, startedAt);
    }

    if (code === "token_invalid" || code === "unauthenticated") {
      // Session is dead — clear it so guards bounce to sign-in immediately.
      if (tokens.getRefresh() || tokens.getAccess()) tokens.endSession();
    }

    throw logFailure(path, opts, toApiError(res, peeked), startedAt);
  }

  if (!res.ok) throw logFailure(path, opts, toApiError(res, await parseBody(res)), startedAt);
  return finish<T>(res, opts);
}

async function finish<T>(
  res: Response,
  opts: RequestOptions,
  path?: string,
  startedAt?: number,
): Promise<T> {
  if (opts.raw) return res as unknown as T;
  const body = await parseBody(res);

  // Opt-in only, for pinning down an undocumented response shape.
  if (path && isRecordingResponses()) {
    recordResponse({
      method: opts.method ?? "GET",
      path,
      status: res.status,
      body,
      durationMs: startedAt === undefined ? undefined : Math.round(performance.now() - startedAt),
    });
  }

  return body as T;
}

/**
 * Records a failure for the in-app diagnostics panel, so a phone with no dev
 * tools can still see the status, the code, the request id and what was sent.
 * `aborted` is skipped — a cancelled query is not a fault.
 */
function logFailure(
  path: string,
  opts: RequestOptions,
  error: ApiError,
  startedAt: number,
): ApiError {
  if (error.code !== "aborted") {
    recordLogEntry({
      kind: "request",
      method: opts.method ?? "GET",
      path,
      status: error.status,
      code: error.code,
      message: error.message,
      requestId: error.requestId,
      details: error.details ?? undefined,
      sentBody: redactBody(path, opts.body),
      durationMs: Math.round(performance.now() - startedAt),
    });
  }
  return error;
}

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, "method" | "body">) =>
    apiFetch<T>(path, { ...opts, method: "GET" }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "method" | "body">) =>
    apiFetch<T>(path, { ...opts, method: "POST", body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "method" | "body">) =>
    apiFetch<T>(path, { ...opts, method: "PUT", body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "method" | "body">) =>
    apiFetch<T>(path, { ...opts, method: "PATCH", body }),
  del: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "method" | "body">) =>
    apiFetch<T>(path, { ...opts, method: "DELETE", body }),
};
